import React, {useEffect, useState} from 'react';
import {FlowLayout, FormField, FormFieldHelperText, FormFieldLabel, Input} from '@salt-ds/core';
import PageNavigation from '../../components/common/page-navigation/PageNavigation';
import Constants from '../../components/common/Constants';
import {useLocation, useNavigate} from 'react-router';
import {getAccessCode, validateDuplicateUserId} from '../../api/ForgotPasswordApi';

export default function ForgotPasswordAuthenticateForm(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({userId: "", email: "", orgId: ""});
  const [lastUserId, setLastUserId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [errors, setErrors] = useState({
    userId: false,
    email: {required: false, invalid: false},
    orgId: false
  });

  // Populate userId from location state if available
  useEffect(() => {
    if (location.state?.formData?.userId) {
      setFormData(prevData => ({
        ...prevData,
        userId: location.state.formData.userId
      }));
      // If we already have a userId, check if we need to show orgId field
      if (location.state.formData.userId.trim() !== '') {
        validateDuplicateUserID(location.state.formData.userId);
      }
    }

    if (location.state?.formData?.orgId) {
      setFormData(prevData => ({
        ...prevData,
        orgId: location.state.formData.orgId
      }));
    }
  }, []);


  const isValidEmail = (email) => {
    const emailRegEx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegEx.test(email);
  }
  const isValidUserId = (userId) => {
    const userIdRegEx = /^[a-zA-Z0-9]+$/;
    return userIdRegEx.test(userId);
  }

  const handleChange = (event) => {
    const {name, value} = event.target;

    setFormData((prevFormData) => ({...prevFormData, [name]: value}));

    //Clear error when user begins to type
    if (name === 'userId') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        userId: false,
      }));
      props.setOrgIdVisible(false);
    } else if (name === 'email') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: {required: false, invalid: false},
      }));
    } else if (name === 'orgId') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        orgId: false,
      }));
    }
  };

  //Validation for empty fields
  const validateFormData = () => {
    return {
      userId: formData.userId.trim() === '' || !isValidUserId(formData.userId),
      email: {
        required: formData.email.trim() === '',
        invalid: formData.email.trim() !== '' && !isValidEmail(formData.email),
      },
      orgId: props.orgIdVisible && formData.orgId.trim() === ''
    };
  };

  const handleSubmit = async (event) => {
    props.setIsLoading(true);
    setIsSubmitting(true);
    event.preventDefault();

    const newErrors = validateFormData();
    setErrors(newErrors);

    //Submit the form
    if (!newErrors.userId && !newErrors.email.required && !newErrors.email.invalid && (!props.orgIdVisible || !newErrors.orgId)) {
      try {
        const response = await getAccessCode(formData.orgId, formData.userId, formData.email);
        if (response.data) {
          sessionStorage.setItem('userId', response.data.userId);
          sessionStorage.setItem('loginName', formData.userId);
          sessionStorage.setItem('orgName', formData.orgId);
          sessionStorage.setItem('accessResourceToken', response.data.accessResourceToken);
          if(response.data.csrf !== null){
            sessionStorage.setItem(response.data.csrf.headerName, response.data.csrf.token);
          }
          navigate(Constants.forgotPasswordProvideAccessCode, {state: {formData, from: Constants.forgotPassword}});
        }
        else if (response.error && response.error.detail) {
          setFormData({userId: "", email: "", orgId: ""});
          props.showErrorOnPage(response.error.detail);
          setIsSubmitting(false);
        }
        else if(response.userLoginStatus==="INACTIVE")
        {
          setFormData({userId: "", email: "", orgId: ""});
          props.showErrorOnPage(Constants.inactiveUserErrorMessage);
          setIsSubmitting(false);
        }
        else{
          setFormData({userId: "", email: "", orgId: ""});
          props.showErrorOnPage(Constants.incorrectUserCredentialsErrorMessage);
          setIsSubmitting(false);
        }
      } catch (error) {
        props.setIsLoading(false);
        setIsSubmitting(false);
        throw error;
      }
        finally {
            props.setIsLoading(false);
        }
    }
    else {
      props.setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const validateDuplicateUserID = async (userId) => {
    if(userId !== "" && lastUserId !== null && userId === lastUserId){
      props.setOrgIdVisible(true);
      return;
    }
    if (userId !== null && userId !== '' && userId.length > 0 && (lastUserId == null || userId !== lastUserId)) {
      try {
        const response = await validateDuplicateUserId(userId);
        if (response.data) {
          props.setOrgIdVisible(true);
          setLastUserId(userId);
        }else if (response.error && response.error.detail) {
          props.setOrgIdVisible(false);
          props.showErrorOnPage(response.error.detail);
        } else {
          props.setOrgIdVisible(false);
        }
      } catch {
        props.setOrgIdVisible(false);
      }
    }
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit} data-testid="forgot-password-form" noValidate>
      <fieldset disabled={isSubmitting} style={{border:0, padding:0, margin:0}}>
      <FlowLayout className="form-layout" gap={3}>
        <FormField validationStatus={errors.userId ? "error" : undefined}>
          <FormFieldLabel htmlFor="userId">{Constants.userIdLabel}</FormFieldLabel>
          <Input id="userId" inputProps={{name: "userId", minLength:6, maxLength:20, "aria-required": "true",
            "aria-describedby": errors.userId ? "userId-error" : undefined,
            onKeyPress: (e) => {
              if (!/^[a-zA-Z0-9]$/.test(e.key)) {
                e.preventDefault();
              }
            }}} value={formData.userId} onChange={handleChange}
            onBlur={() => validateDuplicateUserID(formData.userId)}/>
          {errors.userId && (<FormFieldHelperText id="userId-error" role="alert">{Constants.requiredFieldError}</FormFieldHelperText>)}
        </FormField>

        <FormField validationStatus={(errors.email.required || errors.email.invalid) ? "error" : undefined}>
          <FormFieldLabel htmlFor="email">{Constants.emailAddressLabel}</FormFieldLabel>
          <Input type="email" id="email" inputProps={{name: "email", maxLength:50, "aria-required": "true", "aria-describedby": (errors.email.required || errors.email.invalid) ? "email-error" : undefined}} value={formData.email}
            onChange={handleChange} required autoComplete="off" />
          {errors.email.required ?
            <FormFieldHelperText id="email-error" role="alert">{Constants.requiredFieldError}</FormFieldHelperText> : (errors.email.invalid ?
              <FormFieldHelperText id="email-error" role="alert">{Constants.invalidEmailError}</FormFieldHelperText> : "")}
        </FormField>

        {props.orgIdVisible &&
          <FormField validationStatus={errors.orgId ? "error" : undefined}>
            <FormFieldLabel htmlFor="orgId">{Constants.orgIdLabel}</FormFieldLabel>
            <Input id="orgId" inputProps={{name: "orgId", maxLength:7, "aria-required": "true", "aria-describedby": errors.orgId ? "orgId-error" : undefined}} value={formData.orgId} onChange={handleChange} />
            {errors.orgId && <FormFieldHelperText id="orgId-error" role="alert">{Constants.requiredFieldError}</FormFieldHelperText>}
          </FormField>}
      </FlowLayout>
      <PageNavigation style={{marginTop:"10%"}} />
      </fieldset>
    </form>
  );
}